import { inject } from '@angular/core';
import { Router, type CanDeactivateFn } from '@angular/router';
import { AppolowModalRef } from '@appolow/modal';
import { firstValueFrom } from 'rxjs';
import { ConfirmDialogComponent } from '@shared/components/confirm-dialog/confirm-dialog.component';
import { Translations } from '@core/services';
import { modal } from '@core/services/dialog.service';
import { CanvasState } from '../../page-designer/utilities/canvas-state';

/**
 * Set for one tick after the user confirms "leave": the manual re-navigation
 * re-triggers this guard, and this flag lets that second pass through without
 * re-prompting. Returning `true` asynchronously from the guard doesn't resume
 * the original (already-cancelled) navigation, so we drive it ourselves.
 */
let bypassOnce = false;

/**
 * Blocks navigation away from the page designer while the canvas has unsaved
 * changes, asking the user to confirm ("leave without saving?") first. Reuses
 * the shared `ConfirmDialogComponent`, mirroring `canDeactivateFormFn` but keyed
 * off `CanvasState.dirty` instead of an Angular form.
 */
export const canDeactivateCanvasFn: CanDeactivateFn<unknown> = (
	_component,
	_currentRoute,
	_currentState,
	nextState
) => {
	if (bypassOnce) {
		bypassOnce = false;
		return true;
	}

	const canvas = inject(CanvasState, { optional: true });
	const router = inject(Router);

	// Nothing pending → let navigation through.
	if (!canvas || !canvas.dirty()) {
		return true;
	}

	// A modal is already up (e.g. a save flow) — don't stack another.
	if (modal().hasOpenModals()) {
		return false;
	}

	const modalRef: AppolowModalRef = modal().open(ConfirmDialogComponent, {
		windowClass: 'modal-with-side-background modal-above-loading',
		size: 'lg',
		animation: true,
		backdrop: 'static',
		centered: true,
		headerSelector: '.confirm-dialog__header',
		footerSelector: '.confirm-dialog__footer'
	});

	Object.assign(modalRef.componentInstance, {
		title: Translations.instant('GENERIC.TITLES.UNSAVED_CHANGES'),
		content: Translations.instant(
			'GENERIC.MESSAGES.WARNING.UNSAVED_CHANGES_WARNING'
		),
		showDenyButton: true,
		confirmButtonText: Translations.instant('GENERIC.BUTTONS.LEAVE'),
		denyButtonText: Translations.instant('GENERIC.BUTTONS.STAY'),
		icon: 'warning'
	});

	// Wait for the modal to fully close before re-navigating, so the next guard
	// (modalGuard) doesn't see it still open.
	const modalHidden = firstValueFrom(modalRef.hidden);

	modalRef.result
		.then(async (leave: boolean) => {
			await modalHidden.catch(() => {});
			if (leave && nextState?.url) {
				bypassOnce = true;
				try {
					await router.navigateByUrl(nextState.url);
				} catch {
					bypassOnce = false;
				}
			}
		})
		// Dismissed via ESC / backdrop → treat as "stay".
		.catch(async () => {
			await modalHidden.catch(() => {});
		});

	// Cancel the original navigation; the confirmed one is re-issued above.
	return false;
};
